import type { CSSProperties, ReactNode } from "react";

import type { ChapterBoard } from "@/lib/timeline/board";
import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { colorForCharacterFromMap } from "@/lib/timeline/character/colors";
import { locationRunsForMount } from "@/lib/timeline/location/LocationMarker";
import { locationBoxColors } from "@/lib/timeline/location/LocationMarker/palette";
import { locationRunRole } from "@/lib/timeline/location/LocationRun";
import { BoardModelDebug } from "./BoardModelDebug";

export function boardMarkerDisc(
  characterId: string,
  castById: Map<string, ChapterBoard["cast"][number]>,
  layout: BookTimelineLayout,
): ReactNode {
  const member = castById.get(characterId);
  if (member === undefined) {
    return null;
  }
  return (
    <div
      key={characterId}
      data-marker-part="disc"
      data-character-id={characterId}
      className="timeline-character-marker"
    >
      <div
        className="timeline-marker__disc"
        style={{
          backgroundColor: colorForCharacterFromMap(
            characterId,
            layout.characterColors,
          ),
        }}
      >
        <div className="timeline-marker__face" data-face="from" />
        <div className="timeline-marker__face" data-face="to" />
      </div>
    </div>
  );
}

export function boardCharacterDebugFrames(characterId: string): ReactNode {
  return (
    <>
      <div
        className="board-model-debug-frame"
        data-board-debug-frame=""
        data-board-model="Character"
        data-board-id={characterId}
      />
      <div
        className="board-model-debug-frame"
        data-board-debug-frame=""
        data-board-model="CharacterPortrait"
        data-board-id={characterId}
      />
      <div
        className="board-model-debug-frame"
        data-board-debug-frame=""
        data-board-model="CharacterName"
        data-board-id={characterId}
      />
    </>
  );
}

export function boardLocationStack(
  run: ReturnType<typeof locationRunsForMount>[number],
  index: number,
  board: ChapterBoard,
  boardModelDebug: boolean,
): ReactNode {
  const colors = locationBoxColors(index);
  const boardGroup = board.locationGroups.find(
    (group) => group.branchId === run.runId,
  );
  const label = boardGroup?.site.siteLabel ?? run.locationLabel;
  const markerKind =
    locationRunRole(run) === "secondary"
      ? "LocationSecondary"
      : "LocationPrimary";
  return (
    <div
      key={run.runId}
      data-location-run={run.runId}
      className="timeline-location-stack"
      style={
        {
          "--location-bg": colors.background,
          "--location-border": colors.border,
          "--location-text": colors.text,
        } as CSSProperties
      }
    >
      <BoardModelDebug
        kind={markerKind}
        id={boardGroup?.site.id ?? run.runId}
        debug={boardModelDebug}
      >
        <div className="timeline-location-box">
          <span className="timeline-location-box__label">{label}</span>
        </div>
      </BoardModelDebug>
      <div className="timeline-location-parts" data-location-parts="" />
    </div>
  );
}
